import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  FileSearch,
  Building,
  Wallet,
  Users,
  UserPlus,
  RefreshCw,
  RotateCcw,
  Loader2,
  Sparkles,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { FileExtractionCard } from "./FileExtractionCard";
import { ReExtractDialog } from "./ReExtractDialog";
import {
  ModelSelector,
  ModelBadge,
  getModelById,
  type ModelId,
} from "./ModelSelector";
import type { ExtractionReportResult } from "@shared/discovery-types";

interface ExtractionReviewPanelProps {
  sessionId: number;
  onClose?: () => void;
}

export function ExtractionReviewPanel({
  sessionId,
  onClose,
}: ExtractionReviewPanelProps) {
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null
  );
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [reprocessModel, setReprocessModel] = useState<ModelId>(
    "gemini-3-flash-preview"
  );
  const [showModelSelector, setShowModelSelector] = useState(false);

  const {
    data: report,
    isLoading,
    refetch,
  } = trpc.companyDiscovery.getExtractionReport.useQuery({ sessionId });

  const reprocessMutation = trpc.companyDiscovery.reprocessSession.useMutation({
    onSuccess: () => {
      setIsReprocessing(false);
      refetch();
      // Trigger a page reload to refresh the main preview too
      window.location.reload();
    },
    onError: error => {
      setIsReprocessing(false);
      console.error("Reprocess failed:", error);
    },
  });

  const handleReprocessAll = () => {
    setIsReprocessing(true);
    reprocessMutation.mutate({
      sessionId,
      modelId: reprocessModel !== "custom" ? reprocessModel : undefined,
    });
  };

  // Always render header for accessibility
  const header = (
    <SheetHeader className="px-4 py-4 border-b">
      <SheetTitle className="flex items-center gap-2">
        <FileSearch className="h-5 w-5" />
        Extraction Review
      </SheetTitle>
      <SheetDescription>
        Review what was extracted from each file and troubleshoot missing data
      </SheetDescription>
    </SheetHeader>
  );

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        {header}
        <div className="flex-1 overflow-auto p-4 space-y-6">
          {/* Summary Section Skeleton */}
          <div className="space-y-4">
            <Skeleton className="h-4 w-40 animate-shimmer" />

            {/* Quality Progress Bar Skeleton */}
            <div className="space-y-2">
              <div className="flex justify-between">
                <Skeleton className="h-4 w-24 animate-shimmer" />
                <Skeleton className="h-4 w-10 animate-shimmer" />
              </div>
              <Skeleton className="h-2 w-full animate-shimmer" />
            </div>

            {/* Stats Grid Skeleton */}
            <div className="grid grid-cols-3 gap-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="p-3 bg-muted/50 rounded-lg text-center">
                  <Skeleton className="h-5 w-5 mx-auto mb-1 animate-shimmer" />
                  <Skeleton className="h-6 w-8 mx-auto mb-1 animate-shimmer" />
                  <Skeleton className="h-3 w-16 mx-auto animate-shimmer" />
                </div>
              ))}
            </div>

            {/* What was found Skeleton */}
            <div className="grid grid-cols-2 gap-3">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 p-2 bg-muted/50 rounded"
                >
                  <Skeleton className="h-4 w-4 animate-shimmer" />
                  <Skeleton className="h-4 w-24 animate-shimmer" />
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Per-File Breakdown Skeleton */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-36 animate-shimmer" />
              <Skeleton className="h-8 w-20 animate-shimmer" />
            </div>
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-8 w-8 rounded animate-shimmer" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-40 animate-shimmer" />
                        <Skeleton className="h-3 w-24 animate-shimmer" />
                      </div>
                    </div>
                    <Skeleton className="h-6 w-20 rounded-full animate-shimmer" />
                  </div>
                  <div className="flex gap-2">
                    <Skeleton className="h-5 w-16 rounded-full animate-shimmer" />
                    <Skeleton className="h-5 w-20 rounded-full animate-shimmer" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex flex-col h-full">
        {header}
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <FileSearch className="h-12 w-12 text-muted-foreground" />
          <p className="text-muted-foreground">No extraction data available</p>
        </div>
      </div>
    );
  }

  const { summary, perFile } = report;

  // Calculate quality score
  const qualityScore = Math.round(
    (summary.successfulExtractions / Math.max(summary.totalFiles, 1)) * 100
  );

  return (
    <div className="flex flex-col h-full">
      {header}

      <div className="flex-1 overflow-auto p-4 space-y-6">
        {/* Summary Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold uppercase text-muted-foreground">
            Extraction Summary
          </h3>

          {/* Quality Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Overall Quality</span>
              <span className="font-medium">{qualityScore}%</span>
            </div>
            <Progress value={qualityScore} className="h-2" />
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-success/10 rounded-lg text-center">
              <CheckCircle className="h-5 w-5 text-success mx-auto mb-1" />
              <div className="text-lg font-semibold text-success">
                {summary.successfulExtractions}
              </div>
              <div className="text-xs text-success">Successful</div>
            </div>
            <div className="p-3 bg-warning/10 rounded-lg text-center">
              <AlertTriangle className="h-5 w-5 text-warning mx-auto mb-1" />
              <div className="text-lg font-semibold text-warning">
                {summary.partialExtractions}
              </div>
              <div className="text-xs text-warning">Partial</div>
            </div>
            <div className="p-3 bg-destructive/10 rounded-lg text-center">
              <XCircle className="h-5 w-5 text-destructive mx-auto mb-1" />
              <div className="text-lg font-semibold text-destructive">
                {summary.failedExtractions}
              </div>
              <div className="text-xs text-destructive">Failed</div>
            </div>
          </div>

          {/* What was found */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded">
              <Building className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                Company Info:{" "}
                {summary.companyInfoFound ? (
                  <Badge variant="outline" className="text-success ml-1">
                    Found
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-destructive ml-1">
                    Missing
                  </Badge>
                )}
              </span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded">
              <Wallet className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                Bank Accounts:{" "}
                <span className="font-medium">{summary.bankAccountsCount}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                Vendors:{" "}
                <span className="font-medium">{summary.vendorsCount}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded">
              <UserPlus className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                Customers:{" "}
                <span className="font-medium">{summary.customersCount}</span>
              </span>
            </div>
          </div>

          {/* Missing Critical Fields */}
          {summary.missingCritical.length > 0 && (
            <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
              <h4 className="text-sm font-medium text-destructive mb-2">
                Missing Critical Fields
              </h4>
              <div className="flex flex-wrap gap-2">
                {summary.missingCritical.map(field => (
                  <Badge
                    key={field}
                    variant="outline"
                    className="text-destructive border-destructive/50"
                  >
                    {field}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-destructive/80 mt-2">
                These fields could not be extracted from any document. Try
                re-extracting with hints.
              </p>
            </div>
          )}
        </div>

        <Separator />

        {/* Per-File Breakdown */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold uppercase text-muted-foreground">
              Per-File Breakdown
            </h3>
            <div className="flex items-center gap-2">
              {/* Model Selector for Reprocessing */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <ModelSelector
                        value={reprocessModel}
                        onChange={setReprocessModel}
                        disabled={isReprocessing}
                        variant="inline"
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p className="text-xs">Select model for reprocessing</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReprocessAll}
                disabled={isReprocessing}
              >
                {isReprocessing ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <RotateCcw className="h-3 w-3 mr-1" />
                )}
                {isReprocessing ? "Reprocessing..." : "Reprocess All"}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-3 w-3 mr-1" />
                Refresh
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            {perFile.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No files have been processed yet
              </p>
            ) : (
              perFile.map(result => (
                <FileExtractionCard
                  key={result.documentId}
                  result={result}
                  onReExtract={setSelectedDocumentId}
                  showLLMDetails
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Re-Extract Dialog */}
      <ReExtractDialog
        open={!!selectedDocumentId}
        onOpenChange={open => !open && setSelectedDocumentId(null)}
        sessionId={sessionId}
        documentId={selectedDocumentId || ""}
        fileName={
          perFile.find(r => r.documentId === selectedDocumentId)?.fileName || ""
        }
        onSuccess={() => {
          setSelectedDocumentId(null);
          refetch();
        }}
      />
    </div>
  );
}
